import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  CalendarDays,
  Clock3,
  ClipboardList,
  Image as ImageIcon,
  MapPin,
} from "lucide-react";
import EventFlyer from "../components/EventFlyer";
import EventPageMetadata from "../components/EventPageMetadata";
import EventShareActions from "../components/EventShareActions";
import { getEventFlyerStoragePath, getEventFlyerUrl } from "../eventMedia";
import { normalizeEventRecord } from "../eventViewModel";
import { buildHousingSurveyAbsoluteUrl } from "./config";
import HousingDevelopmentQr from "./HousingDevelopmentQr";
import { subscribeToHousingDevelopmentSurveys } from "./housingDevelopmentService";
import {
  formatHousingDevelopmentEventAddress,
  formatHousingDevelopmentEventDate,
  formatHousingDevelopmentEventTime,
  housingDevelopmentEventDateKey,
  partitionHousingDevelopmentEvents,
  publicHousingDevelopmentEventSurveyLinks,
} from "./housingDevelopmentUtils";
import { londonDateKey } from "../businessNetworkEvents";
import "./HousingDevelopment.css";

function directionsUrl(event = {}) {
  const address = formatHousingDevelopmentEventAddress(event, {
    includeVenue: true,
  });
  return address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : "";
}

function registrationBoundary(value) {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return { dateOnly: value.trim(), instant: null, valid: true };
  }
  const instant = typeof value?.toDate === "function"
    ? value.toDate()
    : value instanceof Date
    ? value
    : new Date(value);
  return {
    dateOnly: "",
    instant,
    valid: instant instanceof Date && !Number.isNaN(instant.getTime()),
  };
}

function registrationWindowIsOpen(event = {}, now = new Date()) {
  const openValue = event.registrationOpenAt
    ?? event.registrationOpensAt
    ?? event.registrationOpen;
  const closeValue = event.registrationCloseAt
    ?? event.registrationClosesAt
    ?? event.registrationClose;

  if (openValue === false || closeValue === true) return false;

  const reference = now instanceof Date && !Number.isNaN(now.getTime())
    ? now
    : new Date();
  const today = londonDateKey(reference);
  if (openValue !== undefined && openValue !== null && openValue !== "" && openValue !== true) {
    const boundary = registrationBoundary(openValue);
    if (
      !boundary.valid
      || (boundary.dateOnly && today < boundary.dateOnly)
      || (boundary.instant && reference.getTime() < boundary.instant.getTime())
    ) {
      return false;
    }
  }
  if (closeValue !== undefined && closeValue !== null && closeValue !== "" && closeValue !== false) {
    const boundary = registrationBoundary(closeValue);
    if (
      !boundary.valid
      || (boundary.dateOnly && today > boundary.dateOnly)
      || (boundary.instant && reference.getTime() >= boundary.instant.getTime())
    ) {
      return false;
    }
  }
  return true;
}

export function safeHousingDevelopmentRegistrationDestination(
  event = {},
  now = new Date()
) {
  if (
    event.registrationEnabled !== true
    || event.published === false
    || !registrationWindowIsOpen(event, now)
    || partitionHousingDevelopmentEvents([event], now).upcomingEvents.length !== 1
  ) {
    return "";
  }
  const value = String(event.registrationPath || event.registrationUrl || "").trim();
  if (value.startsWith("/") && !value.startsWith("//") && !value.includes("\\")) {
    return value;
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" ? parsed.href : "";
  } catch {
    return "";
  }
}

export default function HousingDevelopmentEventDetail({
  event,
  surveys: suppliedSurveys = null,
  now = new Date(),
}) {
  const item = normalizeEventRecord(event);
  const eventDateKey = housingDevelopmentEventDateKey(item);
  const address = formatHousingDevelopmentEventAddress(item);
  const mapUrl = directionsUrl(item);
  const [availableSurveys, setAvailableSurveys] = useState(
    Array.isArray(suppliedSurveys) ? suppliedSurveys : []
  );
  useEffect(() => {
    if (Array.isArray(suppliedSurveys)) {
      setAvailableSurveys(suppliedSurveys);
      return undefined;
    }
    return subscribeToHousingDevelopmentSurveys(
      setAvailableSurveys,
      () => setAvailableSurveys([])
    );
  }, [suppliedSurveys]);
  const surveyLinks = useMemo(
    () => publicHousingDevelopmentEventSurveyLinks(item, availableSurveys),
    [availableSurveys, item]
  );
  const registrationDestination = safeHousingDevelopmentRegistrationDestination(item, now);
  const partners = Array.isArray(item.partnerOrganisations)
    ? item.partnerOrganisations.filter(Boolean)
    : Array.isArray(item.organisations)
    ? item.organisations.filter(Boolean)
    : [];
  const hasPoster = Boolean(
    getEventFlyerUrl(item) || getEventFlyerStoragePath(item)
  );

  return (
    <article className="hd-page hd-event-detail" data-content-type="housing-development-event">
      <EventPageMetadata event={item} />

      <header className="hd-event-hero">
        <div className="hd-shell">
          <p className="hd-kicker">Housing &amp; Development event</p>
          <h1>{item.title}</h1>
          {item.subtitle ? <p className="hd-event-hero__subtitle">{item.subtitle}</p> : null}
          <div className="hd-hero__links">
            {registrationDestination ? (
              registrationDestination.startsWith("/") ? (
                <Link className="hd-button" to={registrationDestination}>Register for this event</Link>
              ) : (
                <a className="hd-button" href={registrationDestination} target="_blank" rel="noreferrer">
                  Register for this event
                </a>
              )
            ) : null}
            <Link className="hd-back-link" to="/housing-development">
              View Housing &amp; Development
            </Link>
          </div>
        </div>
      </header>

      <div className="hd-shell hd-event-stack">
        <section className="hd-event-layout" aria-labelledby="hd-event-details-title">
          <div className="hd-poster-frame">
            {hasPoster ? (
              <EventFlyer event={item} className="hd-poster-media" eager />
            ) : (
              <div className="hd-poster-placeholder" role="img" aria-label="Event poster coming soon">
                <ImageIcon aria-hidden="true" />
                <strong>Event poster coming soon</strong>
              </div>
            )}
          </div>

          <div className="hd-event-facts">
            <p className="hd-eyebrow">Published event information</p>
            <h2 id="hd-event-details-title">Event details</h2>
            <dl className="hd-facts hd-facts--large">
              <div>
                <dt><CalendarDays aria-hidden="true" /> Date</dt>
                <dd><time dateTime={eventDateKey}>{formatHousingDevelopmentEventDate(item)}</time></dd>
              </div>
              <div>
                <dt><Clock3 aria-hidden="true" /> Time</dt>
                <dd>{formatHousingDevelopmentEventTime(item)}</dd>
              </div>
              <div>
                <dt><MapPin aria-hidden="true" /> Venue</dt>
                <dd>
                  {item.venueName || item.location || "Venue to be confirmed"}
                  {address ? <><br />{address}</> : null}
                </dd>
              </div>
            </dl>
            {mapUrl ? (
              <a className="hd-text-link" href={mapUrl} target="_blank" rel="noreferrer">
                View on Google Maps
              </a>
            ) : null}
          </div>
        </section>

        <section className="hd-panel" aria-labelledby="hd-event-information-title">
          <Building2 aria-hidden="true" />
          <h2 id="hd-event-information-title">Event information</h2>
          <p>
            {item.description ||
              "Further event information will be published when it is available."}
          </p>
          <p>
            Questions about this event? Email{" "}
            <a
              className="hd-text-link"
              href={`mailto:info@urbanunitynetwork.com?subject=${encodeURIComponent(item.title)}`}
            >
              info@urbanunitynetwork.com
            </a>.
          </p>
        </section>

        {surveyLinks.length ? (
          <section className="hd-survey-cta" aria-labelledby="hd-event-survey-title">
            <div className="hd-survey-cta__content">
              <ClipboardList aria-hidden="true" />
              <div>
                <p className="hd-eyebrow">Share your views</p>
                <h2 id="hd-event-survey-title">
                  {surveyLinks.length === 1 ? "Complete event survey" : "Event surveys"}
                </h2>
                <p>
                  Complete {surveyLinks.length === 1 ? "the event survey" : "a survey"}
                  {" "}online or scan its QR code with your phone.
                </p>
              </div>
            </div>
            <div className="hd-survey-options">
              {surveyLinks.map((survey, index) => {
                const title = survey.title || (surveyLinks.length === 1
                  ? "Event survey"
                  : `Event survey ${index + 1}`);
                const surveyQrUrl = buildHousingSurveyAbsoluteUrl(
                  survey.slug,
                  "event-qr"
                );
                return (
                  <article className="hd-survey-option" key={survey.path}>
                    {survey.title ? <h3>{survey.title}</h3> : null}
                    <Link className="hd-button hd-button--survey" to={`${survey.path}?source=website`}>
                      {surveyLinks.length === 1 ? "Complete event survey" : `Complete ${title}`}
                    </Link>
                    <div className="hd-survey-qr">
                      <HousingDevelopmentQr
                        url={surveyQrUrl}
                        filename={`${survey.slug}-event-qr`}
                        title={`${title} QR code`}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ) : null}

        <section className="hd-panel" aria-labelledby="hd-event-partners-title">
          <h2 id="hd-event-partners-title">Partner information</h2>
          {partners.length ? (
            <ul className="hd-partner-list">
              {partners.map((partner) => <li key={partner}>{partner}</li>)}
            </ul>
          ) : (
            <p>Collaborating organisations will be published here.</p>
          )}
        </section>

        <EventShareActions event={item} />
      </div>
    </article>
  );
}

